<template>
    <div class="chart-renderer">
        <canvas role="img" :aria-label="name" :aria-describedby="tableId" v-show="hasNonZeroValue" ref="canvas"></canvas>
  <span class="hidden-lg-and-up hidden-lg-and-down" :id="tableId">
        <table>
            <thead>
            <tr>
            <td v-for="dataset in chartData.datasets">{{dataset.label}}</td>
            </tr>
            </thead>
            <tbody>
                <tr v-for="(row, index) in chartData.datasets[0].data">
                    <td>{{chartData.labels[index]}}</td><td v-for="dataset in chartData.datasets">{{dataset.data[index]}}</td>
                </tr>
            </tbody>
        </table>
  </span>
        <div class="canvas-hider">
            <canvas role="img" :aria-label="name" :aria-describedby="accesibleDataId" v-show="hasNonZeroValue" ref="downloadCanvas"></canvas>
        </div>
        <p class="text-center mt-10" v-if="!hasNonZeroValue"> All Values are zero for the selected search criteria.</p>
    </div>
</template>

<script lang="ts">
import {Component, Prop} from 'vue-property-decorator';
import Chart from 'chart.js';
import InfographicComponent from "@/components/base/InfographicComponent";
import {arrayToCsv, downloadAs, Utils} from "@/components/utils/utils";
import {AVERAGE_COLOR, CHART_COLORS} from "@/consts/consts";
import {debounceFn} from "debounce-decorator-ts";
import {Filters} from "@/models/Filters";
import {ChartData, DataItem, DataSet} from "@/models/general";
import * as pattern from 'patternomaly'
import {DataPointTypes} from "@/data/sections";


@Component({})
  export default class ChartRenderer extends InfographicComponent {

    @Prop() private chartData!: ChartData;
    @Prop() private chartType!: string[];
    @Prop() private columnHeaders!: string[];
    @Prop() private chartOptions!: any;
    @Prop() private chartPlugins!: Array<any>;
    @Prop() private name!: string;
    @Prop() private isPrev!: boolean;
    @Prop({type: Object, default: () => ({})}) filters!: Filters;


    private defaultOptions = {
      // animation: false,
      responsive: true,
      maintainAspectRatio: false,
      chartArea: {
        backgroundColor: 'white'
      }

    }
    private mainChartOptions: any = {}

    private pieChartOptionsForDownload = {
      responsive: true,
      maintainAspectRatio: false,
      chartArea: {
        backgroundColor: 'white'
      },
      plugins: {}, tooltips: {
        callbacks: {
          label: function (tooltipItem: any, data: any) {
            var dataLabel = data.labels[tooltipItem.index];
            var value = ' : ' + data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index].toLocaleString() + '%'


            if (dataLabel instanceof Array) {
              dataLabel = dataLabel.slice();
              dataLabel[0] += value;
            } else {
              dataLabel += value;
            }
            return dataLabel;
          }
        }
      },
      legend: {
        onClick: (e: Event) => e.stopPropagation(),
        position: 'left',
        labels: {
          generateLabels: function (chart: Chart) {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              const ds = data.datasets[0];
              return data.labels.map(function (label, i) {
                return {
                  text: label + ' ' + ds.data[i].toString() + '%',
                  strokeStyle: CHART_COLORS[i],
                  fillStyle: CHART_COLORS[i],
                }
              })

            }
            return []
          }
        }
      }
    }


    private hasNonZeroValue: boolean = true;

    private chart?: Chart
    private downLoadChart?: any

    mounted() {

      this.setChartDataColors(this.chartData, this.chartType)
      this.drawChart()
      this.mainChartOptions = Object.assign(this.defaultOptions, this.chartOptions)

      this.mainChartOptions.legend.onClick = this.legendClicked
      this.hasNonZeroValue = this.chartData.datasets.some(dataset => (dataset.data as number[]).some(num => num > 0))

      // window.addEventListener('resize', this.drawChart);

    }

    get tableId(){
        return this.name.split(' ').map(s => s.trim()).join('-')
    }

    legendClicked(e: any, elm: any){
        const datasets: DataSet[] = this.chartData.datasets.filter(d => d.label.indexOf(elm.text) == 0)
        datasets.forEach(d => d.hidden = !d.hidden)
        this.drawChart()
    }

    unmounted() {
      if (this.chart) {
        this.chart.destroy();
      }
      if (this.downLoadChart) {
        this.downLoadChart.destroy();
      }

      // window.removeEventListener('resize', this.drawChart);
    }

    @debounceFn()
    drawChart() {

      if (this.chart) {
        this.chart.destroy();
      }
      const canvas = this.$refs.canvas as HTMLCanvasElement
      if (canvas) {
        this.chart = this.createChart(this.$refs.canvas as HTMLCanvasElement, this.mainChartOptions)

        if (this.chartType[0] === 'pie') {
          this.downLoadChart = this.createChart(this.$refs.downloadCanvas as HTMLCanvasElement, this.pieChartOptionsForDownload)
        }
      } //else{
      //    console.error('missing canvas element',  {canvas, name: name, data: this.chartData})
      // }

    }

    get accesibleDataId(){
        return this.name.split(' ').map(word => word.trim).join('-')
    }

    private createChart(canvas: HTMLCanvasElement, options: Object) {
      return new Chart(canvas, {
        type: this.chartType[0],
        data: this.chartData,
        options,
        plugins: this.chartPlugins
      });

    }

    public download(mode: string,dataItem:DataItem) {
      return mode === 'csv' ? this.downloadCsv(dataItem) : this.downloadImage()
    }

    public downloadImage() {
      if (this.downLoadChart) {
        downloadAs(this.downLoadChart.toBase64Image(), this.name)
      } else if (this.chart) {
        downloadAs(this.chart.toBase64Image(), this.name)
      }
    }


    public downloadCsv(dataItem:DataItem) {
      let results:any = this.columnHeaders;
      let columnHeaders:Array<string> = [...this.columnHeaders];
      let dates:Array<string> =[""]
      let chartDatasets:Array<DataSet> = [...this.chartData.datasets]

      if(this.filters.includeWoWValues){
        let first = columnHeaders.shift();
        let result = columnHeaders.map(i => [i, '']).flat();
        columnHeaders.forEach(value => {
          dates.push(
            Utils.dateFormat(dataItem.periodStart) +' - '+ Utils.dateFormat(dataItem.periodEnd),
            Utils.datePreviousFormat(dataItem.periodStart) +' - '+Utils.datePreviousFormat(dataItem.periodEnd))
        })

        result.unshift(first);
        results= [result,dates];
        if([DataPointTypes.CONS_PLAT_DAY,DataPointTypes.CONS_PLAT_HOUR].indexOf(dataItem.chartName)!== -1){
          chartDatasets= Utils.splitAndMergedArr(this.chartData.datasets);

        }
      }
      const region = this.filters.region.name
      const vertical = this.filters.category.name
      const timePeriod = this.filters.period.name.split(',').join(' ')
      const header = [...results]
      const rows = this.chartData.labels.map((l, i) => [l, ...chartDatasets.map(d => d.data[i])])
      if(this.filters.includeWoWValues) {
        rows.unshift(...header)

      }
      else {
        rows.unshift(header)

      }
      rows.unshift([''])
      rows.unshift(['Time Period: ' + timePeriod])
      rows.unshift(['Region: ' + region])
      rows.unshift(['Vertical: ' + vertical])

      const csv = arrayToCsv(rows)
      downloadAs(csv, this.name + '.csv')

    }

    private setChartDataColors(data: ChartData, chartType: string[]) {
      let hasAverage = data.labels.indexOf('Average') !== -1
      let othersColors = CHART_COLORS.slice(0, data.labels.length - 1)
      othersColors.push(AVERAGE_COLOR)
      if (chartType[0] === 'line') {
        data.datasets.forEach((d: any, index: number) => {
          d.fill = false
          d.backgroundColor = CHART_COLORS[index % 3];
          d['borderColor'] = CHART_COLORS[index % 3]
        })
      }

      if(this.filters.includeWoWValues && this.isPrev){

        data.datasets.forEach((d: any, index: number) => {
            d.backgroundColor = d.data.map((d:any,index:any) =>pattern.draw('diagonal-right-left',CHART_COLORS[index]));
        })
      }

      else {
          data.datasets.forEach((d: any, index: number) => {
              if (!d.backgroundColor)
                  if (!hasAverage) {
                      d.backgroundColor = CHART_COLORS;
                      d.fill = false

                  } else {
                      d.backgroundColor = othersColors
                      d.fill = false
                  }
          })

      }
    }
  }

</script>

<style scoped lang="scss">

    .chart-renderer {

        position: relative;
        height: 400px;
        width: 100%;

        canvas {
            z-index: 1;
            top: 0;
            position: relative;
        }

        .canvas-hider {
            width: 100%;
            height: 100%;
            position: absolute;
            background: red;
            z-index: -1;
            top: 0;
            left: 0;
            visibility: hidden;


        }
    }


</style>

